/**
 * Início (/dashboard): "where I left off and what waits for me" (docs/refactor/09-inicio.md).
 *
 *  - Configuração, until it is all done: the setup checklist.
 *  - Agora: the person's agents running, on this machine (desktop) and elsewhere, with what each is doing.
 *  - Esperando você: the first three things in the inbox.
 *  - Continuar: the latest conversation in each project, one click back to it.
 *  - Projetos: how ready each one is (from "O que falta"), whether it has an MVP, the next stage, and
 *    its pending list.
 *  - Hoje: the person's own time and cost today.
 *
 * The team's numbers are for managers: the first screen of Acompanhar is the Engineering Floor, a
 * room with no place for a row of figures, so they stay here, below the person's own, for managers only.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, ArrowRight, Bug, CircleDollarSign, FlaskConical, MessageSquareText, ShieldAlert, Users } from 'lucide-react'
import { api } from '../lib/api'
import { atLeast, getSession } from '../lib/auth'
import { useI18n } from '../lib/i18n'
import { formatCompact, formatCost, formatDuration, formatNumber, formatRelative } from '../lib/format'
import { realtime } from '../lib/realtime'
import { redock } from '../lib/dockPlacement'
import { useRunnerStatus } from '../lib/runnerHost'
import { useActiveRepository } from '../lib/activeRepository'
import { ASKS_FOR_YOU, awayRecap, firstWaiting, lastVisit, latestPerRepository, runningOf, todayWindow } from '../lib/home'
import { CostValue, EmptyState, Panel, Skeleton, Stat, Badge, Avatar, toneForState } from '../components/primitives'
import type { InboxItem, LiveSession } from '../lib/types'
import type { RepoRunnerStatus } from '../lib/localRunner'
import { SetupChecklist } from '../components/SetupChecklist'
import { GetTheApp } from '../components/GetTheApp'
import { SimpleHome } from '../components/SimpleHome'
import { useSimpleMode } from '../lib/simpleMode'
import { ProjectCard } from '../components/ProjectCard'

export default function Dashboard() {
  const queryClient = useQueryClient()
  const canManage = atLeast(getSession()?.role, 'EngineeringManager')
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const developerId = me?.developerId ?? null
  const simple = useSimpleMode()

  // Session updates patch the live list in place, so one row repaints rather than the list refetching.
  useEffect(
    () =>
      realtime.onSessions((updates) => {
        queryClient.setQueryData<LiveSession[]>(['live'], (current) => {
          if (!current) return current
          const byId = new Map(current.map((s) => [s.workSessionId, s]))
          for (const update of updates) byId.set(update.workSessionId, update)
          return [...byId.values()]
            .filter((s) => !s.isTerminal || Date.now() - new Date(s.lastActivityAt).getTime() < 5 * 60_000)
            .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt))
        })
      }),
    [queryClient],
  )

  return (
    <div className="stack stack--loose home">
      {/* In a browser, with no AI working for the person yet: get the app, first (51, N1). */}
      <GetTheApp />

      {/* Modo simples: what the AI can do, as cards, before anything else (51, N5). */}
      {simple && <SimpleHome />}

      {/* What is left to set up, from the organization's real state; goes away once it is all done. */}
      <SetupChecklist />

      <div className="grid grid--two">
        <NowPanel developerId={developerId} />
        {developerId && <WaitingPanel />}
        {developerId && <ContinuePanel developerId={developerId} />}
        {developerId && <TodayPanel developerId={developerId} />}
      </div>

      <ProjectsPanel />

      {canManage && <TeamToday />}
    </div>
  )
}

// ------------------------------------------------------------------------------ Agora

function NowPanel({ developerId }: { developerId: string | null }) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const live = useQuery({ queryKey: ['live'], queryFn: () => api.dashboard.live() })
  // The same query as the inbox badge; the recap reads what arrived since the last visit (41 §4.1).
  const inbox = useQuery({ queryKey: ['inbox'], queryFn: api.inbox.list, refetchInterval: 20_000, retry: false })
  const [since] = useState(lastVisit)
  const recap = useMemo(() => awayRecap(inbox.data?.items ?? [], since), [inbox.data, since])
  // The person's agents: the developer they are and the agents they made, each its own character.
  const { data: agents } = useQuery({ queryKey: ['my-agents'], queryFn: api.me.agents, enabled: Boolean(developerId), retry: false })
  const lanes = useRunnerStatus().filter((lane) => lane.state === 'working')

  const mine = useMemo(() => {
    const ids = new Set<string>()
    if (developerId) ids.add(developerId)
    for (const agent of agents ?? []) ids.add(agent.developerId)
    // A run on this machine is also reported to the API; it is shown once, as this machine's.
    const here = new Set(lanes.map((lane) => lane.repository.toLowerCase()))
    return runningOf(live.data ?? [], ids).filter((s) => !s.repositoryName || !here.has(s.repositoryName.toLowerCase()))
  }, [live.data, agents, developerId, lanes])

  const nothing = !live.isLoading && lanes.length === 0 && mine.length === 0
  return (
    <Panel
      title={t('home.now')}
      subtitle={t('home.nowHint')}
      actions={<Link to="/track/floor" className="btn btn--sm btn--ghost">{t('home.seeAll')}</Link>}
      flush
    >
      {live.isLoading && lanes.length === 0 ? (
        <div className="stack" style={{ padding: 'var(--s4)' }}>
          <Skeleton height={44} />
        </div>
      ) : nothing && recap ? (
        <div className="stack home__away">
          <p className="small home__away-line">
            <strong>{t('home.away')}</strong>{' '}
            {[
              recap.done.length > 0 && t('home.away.done', { count: recap.done.length }),
              recap.failed > 0 && t('home.away.failed', { count: recap.failed }),
              recap.asks > 0 && t('home.away.asks', { count: recap.asks }),
              recap.published > 0 && t('home.away.published', { count: recap.published }),
            ].filter(Boolean).join(' · ')}
          </p>
          <div className="stack stack--tight home__rows">
            {recap.done.slice(0, 4).map((item) => (
              <button key={item.key} type="button" className="home__row" onClick={() => navigate(item.link)}>
                <Badge tone="ok">{t('inbox.kind.done')}</Badge>
                <span className="stack home__row-main">
                  <span className="bold truncate">{item.title}</span>
                  <span className="tiny muted truncate">{item.repository ? `${item.repository} · ` : ''}{formatRelative(item.at, locale)}</span>
                </span>
              </button>
            ))}
          </div>
          {(recap.asks > 0 || recap.failed > 0) && (
            <Link to="/inbox" className="btn btn--sm home__away-cta">{t('home.away.see')}</Link>
          )}
        </div>
      ) : nothing ? (
        <EmptyState
          title={t('home.nowEmpty')}
          body={t('home.nowEmptyHint')}
          action={<button type="button" className="btn btn--sm" onClick={redock}><MessageSquareText size={14} /> {t('palette.action.prompt')}</button>}
        />
      ) : (
        <div className="stack stack--tight home__rows">
          {lanes.map((lane) => <LaneRow key={`${lane.repository}:${lane.lane ?? 1}`} lane={lane} />)}
          {mine.slice(0, 6).map((session) => <SessionRow key={session.workSessionId} session={session} />)}
        </div>
      )}
    </Panel>
  )
}

/** What a lane on this machine is doing: its last tool, or the last line it printed. */
function lastDoing(lane: RepoRunnerStatus): string | null {
  const stream = lane.stream ?? []
  for (let i = stream.length - 1; i >= 0; i--) {
    const item = stream[i]!
    if (item.kind === 'tool') return item.detail ? `${item.name} · ${item.detail}` : item.name
  }
  return lane.log[lane.log.length - 1] ?? null
}

function LaneRow({ lane }: { lane: RepoRunnerStatus }) {
  const { t } = useI18n()
  return (
    // The prompt box shows the run as it happens; this brings it up.
    <button type="button" className="home__row" onClick={redock}>
      <span className="home__pulse" aria-hidden />
      <span className="stack home__row-main">
        <span className="bold truncate">
          {lane.repository}
          {(lane.lane ?? 1) > 1 && <span className="muted"> · {t('home.lane', { lane: lane.lane ?? 1 })}</span>}
        </span>
        <span className="tiny muted truncate mono">{lastDoing(lane) ?? lane.streamPrompt ?? '—'}</span>
      </span>
      <Badge tone="accent">{t('home.onThisMachine')}</Badge>
    </button>
  )
}

function SessionRow({ session, showDeveloper = false }: { session: LiveSession; showDeveloper?: boolean }) {
  const { t, locale } = useI18n()
  return (
    <Link to={`/work/${session.workSessionId}`} className="home__row">
      <Avatar name={session.developerName} color={session.avatarColor} size="sm" />
      <span className="stack home__row-main">
        <span className="row row--tight">
          <span className="bold truncate">{showDeveloper ? session.developerName : session.repositoryName ?? session.developerName}</span>
          {session.taskExternalId && <span className="mono tiny muted nowrap">{session.taskExternalId}</span>}
          {session.isSimulated && <Badge tone="demo">{t('common.demoDataShort')}</Badge>}
        </span>
        <span className="tiny muted truncate">{session.currentStepNote ?? session.currentStepTitle ?? session.taskTitle ?? '—'}</span>
      </span>
      <Badge tone={toneForState(session.activityState)}>{t(`state.${session.activityState}`)}</Badge>
      <span className="tiny muted nowrap home__when">{formatDuration(session.durationSeconds, locale)}</span>
    </Link>
  )
}

// ------------------------------------------------------------------------------ Esperando você

function WaitingPanel() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // The same query as the inbox's count in the sidebar: one request for both.
  const { data, isLoading } = useQuery({ queryKey: ['inbox'], queryFn: api.inbox.list, refetchInterval: 20_000, retry: false })
  const read = useMutation({ mutationFn: (keys: string[]) => api.inbox.read(keys), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox'] }) })
  const items = firstWaiting(data?.items ?? [], 3)
  const asking = (data?.items ?? []).filter((item) => ASKS_FOR_YOU.includes(item.kind)).length
  const open = (item: InboxItem) => {
    if (!item.read) read.mutate([item.key])
    navigate(item.link)
  }

  return (
    <Panel
      title={t('home.waiting')}
      actions={<Link to="/inbox" className="btn btn--sm btn--ghost">{asking > items.length ? t('home.waitingAll', { count: asking }) : t('inbox.title')}</Link>}
      flush
    >
      {isLoading ? (
        <div className="stack" style={{ padding: 'var(--s4)' }}><Skeleton height={44} /></div>
      ) : items.length === 0 ? (
        <EmptyState title={t('inbox.empty')} body={t('inbox.emptyHint')} />
      ) : (
        <div className="stack stack--tight home__rows">
          {items.map((item) => (
            <button key={item.key} type="button" className={`home__row${item.read ? '' : ' is-unread'}`} onClick={() => open(item)}>
              <Badge tone={item.kind === 'needsYou' || item.kind === 'published' ? 'warn' : item.kind === 'failed' ? 'danger' : item.kind === 'done' ? 'ok' : 'neutral'}>{t(`inbox.kind.${item.kind}`)}</Badge>
              <span className="stack home__row-main">
                <span className="bold truncate">{item.title}</span>
                <span className="tiny muted truncate">{item.repository ? `${item.repository} · ` : ''}{formatRelative(item.at, locale)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Panel>
  )
}

// ------------------------------------------------------------------------------ Continuar

function ContinuePanel({ developerId }: { developerId: string }) {
  const { t, locale } = useI18n()
  const { data, isLoading } = useQuery({
    queryKey: ['home', 'continue', developerId],
    queryFn: () => api.sessions.list({ developerId, pageSize: 40 }),
    staleTime: 30_000,
    retry: false,
  })
  const sessions = latestPerRepository(data?.items ?? [], 5)

  return (
    <Panel title={t('home.continue')} actions={<Link to="/track/sessions" className="btn btn--sm btn--ghost">{t('home.seeAll')}</Link>} flush>
      {isLoading ? (
        <div className="stack" style={{ padding: 'var(--s4)' }}><Skeleton height={44} /></div>
      ) : sessions.length === 0 ? (
        <EmptyState title={t('home.continueEmpty')} body={t('home.continueEmptyHint')} />
      ) : (
        <div className="stack stack--tight home__rows">
          {sessions.map((session) => (
            <Link key={session.workSessionId} to={`/work/${session.workSessionId}`} className="home__row">
              <span className="stack home__row-main">
                <span className="bold truncate">{session.repositoryName ?? t('home.noProject')}</span>
                <span className="tiny muted truncate">{session.taskTitle ?? session.currentStepNote ?? session.currentStepTitle ?? t('home.continueBare')}</span>
              </span>
              <span className="tiny muted nowrap home__when">{formatRelative(session.lastActivityAt, locale)}</span>
              <ArrowRight size={14} className="muted" aria-hidden />
            </Link>
          ))}
        </div>
      )}
    </Panel>
  )
}

// ------------------------------------------------------------------------------ Hoje

function TodayPanel({ developerId }: { developerId: string }) {
  const { t, locale } = useI18n()
  const window_ = useMemo(() => todayWindow(), [])
  // The same query as the person's day page, so opening it from here is instant.
  const { data, isLoading } = useQuery({
    queryKey: ['developer-day', developerId, window_.date],
    queryFn: () => api.developers.day(developerId, { from: window_.from, to: window_.to, includeSimulated: true }),
    refetchInterval: 60_000,
    retry: false,
  })
  const totals = data?.totals

  return (
    <Panel title={t('home.today')} subtitle={t('home.todayHint')} actions={<Link to="/track/day" className="btn btn--sm btn--ghost">{t('home.myDay')}</Link>}>
      {isLoading || !totals ? (
        <Skeleton height={72} />
      ) : (
        <div className="grid home__today">
          <Stat label={t('home.todayTime')} value={formatDuration(totals.activeSeconds, locale)} />
          <Stat
            label={t('home.todayCost')}
            value={
              <CostValue
                value={totals.costUsd}
                accuracy={totals.costAccuracy}
                formatted={formatCost(totals.costUsd, locale)}
                unavailableLabel="—"
                hint={t(totals.costAccuracy === 'Actual' ? 'cost.actualHint' : totals.costAccuracy === 'Estimated' ? 'cost.estimatedHint' : 'cost.unavailableHint')}
              />
            }
          />
          <Stat label={t('home.todaySessions')} value={formatNumber(totals.sessions, locale)} />
          <Stat label={t('home.todayFiles')} value={formatNumber(totals.filesChanged, locale)} />
        </div>
      )}
    </Panel>
  )
}

// ------------------------------------------------------------------------------ Projetos

function ProjectsPanel() {
  const { t } = useI18n()
  const { linked, active, choose, loaded } = useActiveRepository()
  const { data: orders } = useQuery({ queryKey: ['work-orders'], queryFn: api.workOrders.list, retry: false })

  return (
    <Panel title={t('home.projects')} subtitle={t('home.projectsHint')} actions={<Link to="/agents/repositories" className="btn btn--sm btn--ghost">{t('home.seeAll')}</Link>}>
      {!loaded ? (
        <Skeleton height={72} />
      ) : linked.length === 0 ? (
        <EmptyState
          title={t('home.projectsEmpty')}
          body={t('home.projectsEmptyHint')}
          action={
            <>
              <Link to="/start-project" className="btn btn--sm btn--primary" title={t('openProjects.newProjectHint')}>{t('home.startProject')}</Link>
              <Link to="/agents/repositories" className="btn btn--sm btn--ghost">{t('home.projectsLink')}</Link>
            </>
          }
        />
      ) : (
        <div className="home__projects">
          {linked.map((repository) => (
            <ProjectCard key={repository.id} repository={repository} orders={orders ?? []} isActive={repository.id === active?.id} onUse={() => choose(repository.id)} />
          ))}
        </div>
      )}
    </Panel>
  )
}

// ------------------------------------------------------------------------------ the team, for managers

function TeamToday() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const summary = useQuery({ queryKey: ['dashboard', 'summary'], queryFn: () => api.dashboard.summary() })
  const live = useQuery({ queryKey: ['live'], queryFn: () => api.dashboard.live() })

  // Counter changes are throttled in the realtime client; one becomes a refetch here.
  useEffect(
    () =>
      realtime.onCounters(() => {
        void queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] })
      }),
    [queryClient],
  )

  const data = summary.data
  const blocked = (live.data ?? []).filter((s) => !s.isTerminal && s.activityState === 'Blocked')

  return (
    <section className="stack home__team" aria-label={t('home.team')}>
      <div className="row row--between">
        <div className="stack" style={{ gap: 2 }}>
          <h2 className="panel__title">{t('home.team')}</h2>
          <span className="tiny muted">{t('home.teamHint')}</span>
        </div>
        <Link to="/track/floor" className="btn btn--sm btn--ghost">{t('nav.area.track')}</Link>
      </div>

      <div className="grid grid--stats">
        {summary.isLoading || !data ? (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={88} />)
        ) : (
          <>
            <Stat icon={<Users size={12} />} label={t('dashboard.activeDevelopers')} value={formatNumber(data.activeDevelopers, locale)} />
            <Stat icon={<Activity size={12} />} label={t('dashboard.activeSessions')} value={formatNumber(data.activeSessions, locale)} tone="accent" />
            <Stat label={t('dashboard.tasksInProgress')} value={formatNumber(data.tasksInProgress, locale)} />
            <Stat label={t('dashboard.tasksCompletedToday')} value={formatNumber(data.tasksCompletedToday, locale)} tone="ok" />
            <Stat icon={<Bug size={12} />} label={t('dashboard.bugsFixedToday')} value={formatNumber(data.bugsFixedToday, locale)} tone="ok" />
            <Stat label={t('dashboard.aiUsageToday')} value={formatCompact(data.aiTokensToday, locale)} />
            <Stat
              icon={<CircleDollarSign size={12} />}
              label={t('dashboard.aiCostToday')}
              value={
                <CostValue
                  value={data.aiCostToday}
                  accuracy={data.aiCostAccuracy}
                  formatted={formatCost(data.aiCostToday, locale)}
                  unavailableLabel="—"
                  hint={t(data.aiCostAccuracy === 'Actual' ? 'cost.actualHint' : data.aiCostAccuracy === 'Estimated' ? 'cost.estimatedHint' : 'cost.unavailableHint')}
                />
              }
              foot={t(`cost.${data.aiCostAccuracy}`)}
            />
            <Stat
              icon={<FlaskConical size={12} />}
              label={t('dashboard.testsToday')}
              value={formatNumber(data.testsRunToday, locale)}
              foot={t('dashboard.testsPassedOf', { passed: formatNumber(data.testsPassedToday, locale), total: formatNumber(data.testsRunToday, locale) })}
              tone={data.testsFailedToday > 0 ? 'warn' : undefined}
            />
          </>
        )}
      </div>

      <div className="grid grid--two">
        {blocked.length > 0 && (
          <Panel title={<span className="row row--tight"><ShieldAlert size={15} color="var(--danger)" />{t('dashboard.blockedWork')}</span>} flush>
            <div className="stack stack--tight home__rows">
              {blocked.map((session) => <SessionRow key={session.workSessionId} session={session} showDeveloper />)}
            </div>
          </Panel>
        )}
        <Panel title={t('dashboard.costTrend')}>
          {data ? <CostTrend points={data.costTrend} /> : <Skeleton height={140} />}
        </Panel>
      </div>
    </section>
  )
}

/**
 * A bar chart drawn as divs: fourteen bars do not justify a charting library. The values are a
 * table for screen readers, since a row of divs cannot be read aloud usefully.
 */
function CostTrend({ points }: { points: Array<{ date: string; costUsd: number; tokens: number; sessions: number }> }) {
  const { locale } = useI18n()
  const max = Math.max(...points.map((p) => p.costUsd), 0.01)

  return (
    <div>
      <div className="row" style={{ gap: 4, alignItems: 'flex-end', height: 120 }} aria-hidden="true">
        {points.map((point) => (
          <div
            key={point.date}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}
            title={`${point.date}: ${formatCost(point.costUsd, locale)}`}
          >
            <div
              style={{
                height: `${Math.max(2, (point.costUsd / max) * 100)}%`,
                background: point.costUsd > 0 ? 'var(--accent)' : 'var(--border)',
                borderRadius: '2px 2px 0 0',
                transition: 'height var(--slow) var(--ease)',
              }}
            />
          </div>
        ))}
      </div>

      <div className="row row--between tiny muted" style={{ marginTop: 'var(--s2)' }}>
        <span>{points[0]?.date}</span>
        <span>{points[points.length - 1]?.date}</span>
      </div>

      <table className="sr-only">
        <caption>AI cost by day</caption>
        <tbody>
          {points.map((point) => (
            <tr key={point.date}>
              <th scope="row">{point.date}</th>
              <td>{formatCost(point.costUsd, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
